import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import capitalizeEachFirstWord from "../../utils/CapLetters";
import { CHAT_ROOMS } from "../../config/index";
import useApp from "../../store/contexts/AppContext";

function RoomJoinRequestsMessage({ request }) {
  const { otherUser, room, id } = request;
  const { adminCheckInUserIntoRoom } = useApp();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const handleStatus = (status) => {
    adminCheckInUserIntoRoom({ id, room_id: room.id, user_id: otherUser.id, status });
  };

  const firstLetter = otherUser.username?.[0]?.toUpperCase() || "?";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        px: "14px",
        py: "10px",
        borderRadius: "10px",
        mb: "8px",
        "&:last-child": { mb: 0 },
        background: isDark ? alpha("#fff", 0.04) : alpha("#000", 0.03),
        border: `1px solid ${isDark ? alpha("#fff", 0.07) : alpha("#000", 0.07)}`,
      }}
    >
      {/* Avatar */}
      <Avatar
        src={otherUser.avatar || otherUser.profile_image_url || undefined}
        sx={{
          width: 38,
          height: 38,
          fontSize: "15px",
          fontWeight: 700,
          flexShrink: 0,
          // White background handles transparent PNGs; falls back to orange gradient for letter avatars
          bgcolor: (otherUser.avatar || otherUser.profile_image_url) ? "#ffffff" : undefined,
          background: !(otherUser.avatar || otherUser.profile_image_url)
            ? `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.6)})`
            : undefined,
        }}
      >
        {firstLetter}
      </Avatar>

      {/* Text */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 600,
            color: isDark ? "#fff" : theme.palette.text.primary,
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {capitalizeEachFirstWord(otherUser.username)}
        </Typography>
        <Typography
          sx={{
            fontSize: "12px",
            color: isDark ? alpha("#fff", 0.45) : alpha("#000", 0.45),
            mt: "1px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          wants to join{" "}
          <Box component="span" sx={{ color: theme.palette.primary.main, fontWeight: 600 }}>
            {room.name}
          </Box>
        </Typography>
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: "flex", gap: "6px", flexShrink: 0 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<CheckIcon sx={{ fontSize: "14px !important" }} />}
          onClick={() => handleStatus(CHAT_ROOMS.JOIN_REQUEST.APPROVED)}
          sx={{
            borderRadius: "7px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "13px",
            minHeight: "unset",
            minWidth: "unset",
            px: "10px",
            py: "5px",
            background: "linear-gradient(135deg, #43a047, #388e3c)",
            boxShadow: "0 2px 6px rgba(67,160,71,0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #388e3c, #2e7d32)",
              boxShadow: "0 3px 10px rgba(67,160,71,0.4)",
            },
          }}
        >
          Accept
        </Button>
        <Button
          variant="contained"
          size="small"
          startIcon={<CloseIcon sx={{ fontSize: "14px !important" }} />}
          onClick={() => handleStatus(CHAT_ROOMS.JOIN_REQUEST.REJECTED)}
          sx={{
            borderRadius: "7px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "13px",
            minHeight: "unset",
            minWidth: "unset",
            px: "10px",
            py: "5px",
            background: "linear-gradient(135deg, #e53935, #c62828)",
            boxShadow: "0 2px 6px rgba(229,57,53,0.3)",
            "&:hover": {
              background: "linear-gradient(135deg, #c62828, #b71c1c)",
              boxShadow: "0 3px 10px rgba(229,57,53,0.4)",
            },
          }}
        >
          Reject
        </Button>
      </Box>
    </Box>
  );
}

export default RoomJoinRequestsMessage;
